/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import { themeable } from '@instructure/ui-themeable';
import moment from 'moment-timezone';
import { Button } from '@instructure/ui-buttons';
import { Pill } from '@instructure/ui-elements';
import { PresentationContent, ScreenReaderContent } from '@instructure/ui-a11y';
import { IconXLine } from '@instructure/ui-icons';
import { bool, string, number, func, object } from 'prop-types';
import { getFullDateAndTime } from '../../utilities/dateUtils';
import formatMessage from '../../format-message';
import { animatable } from '../../dynamic-ui';
const styles = {
  componentId: 'eAJaG',
  template: function template(theme) {
    return "\n\n.Opportunity-styles__root {\n  position: relative;\n  font-size: ".concat(theme.fontSize || 'inherit', ";\n  font-family: ").concat(theme.fontFamily || 'inherit', ";\n  font-weight: ").concat(theme.fontWeight || 'inherit', ";\n  color: ").concat(theme.color || 'inherit', ";\n  background: ").concat(theme.background || 'inherit', ";\n  padding: ").concat(theme.padding || 'inherit', ";\n  box-sizing: border-box;\n  line-height: ").concat(theme.lineHeight || 'inherit', ";\n}\n\n.Opportunity-styles__oppNameAndTitle {\n  max-width: 16.5rem;\n}\n.Opportunity-styles__oppName {\n  box-sizing: border-box;\n  min-width: 1px;\n  flex: 1;\n  padding-top: ").concat(theme.namePaddingTop || 'inherit', ";\n  text-transform: uppercase;\n  letter-spacing: 0.0625rem;\n  color: ").concat(theme.secondaryColor || 'inherit', ";\n  font-size: ").concat(theme.nameFontSize || 'inherit', ";\n  margin-right: ").concat(theme.closeButtonIconSize || 'inherit', ";\n}\n\n.Opportunity-styles__title {\n  margin-bottom: ").concat(theme.titleMargin || 'inherit', ";\n}\n\n.Opportunity-styles__close {\n  position: absolute;\n  top: 0;\n  offset-inline-end: 0;\n}\n\n[dir=\"ltr\"] .Opportunity-styles__close {\n  right: 0;\n}\n\n[dir=\"rtl\"] .Opportunity-styles__close {\n  left: 0;\n}\n\n.Opportunity-styles__oppName,\n.Opportunity-styles__title {\n  overflow-wrap: break-word;\n  word-wrap: break-word;\n  -webkit-hyphens: auto;\n      -ms-hyphens: auto;\n          hyphens: auto;\n}\n\n.Opportunity-styles__footer {\n  box-sizing: border-box;\n  display: flex;\n  align-items: flex-end;\n  justify-content: space-between;\n  -webkit-padding-end: ").concat(theme.footerPadding || 'inherit', ";\n          padding-inline-end: ").concat(theme.footerPadding || 'inherit', ";\n}\n\n[dir=\"ltr\"] .Opportunity-styles__footer {\n  padding-right: ").concat(theme.footerPadding || 'inherit', ";\n}\n\n[dir=\"rtl\"] .Opportunity-styles__footer {\n  padding-left: ").concat(theme.footerPadding || 'inherit', ";\n}\n\n.Opportunity-styles__status {\n  box-sizing: border-box;\n  min-width: 1px;\n  flex-grow: 1;\n  -webkit-padding-end: ").concat(theme.statusPadding || 'inherit', ";\n          padding-inline-end: ").concat(theme.statusPadding || 'inherit', ";\n}\n\n[dir=\"ltr\"] .Opportunity-styles__status {\n  padding-right: ").concat(theme.statusPadding || 'inherit', ";\n}\n\n[dir=\"rtl\"] .Opportunity-styles__status {\n  padding-left: ").concat(theme.statusPadding || 'inherit', ";\n}\n\n.Opportunity-styles__points {\n  box-sizing: border-box;\n  min-width: 1px;\n  flex-shrink: 0;\n  text-align: end;\n  color: ").concat(theme.secondaryColor || 'inherit', ";\n  text-transform: uppercase;\n  font-size: ").concat(theme.pointsFontSize || 'inherit', ";\n  line-height: ").concat(theme.pointsLineHeight || 'inherit', ";\n}\n\n[dir=\"ltr\"] .Opportunity-styles__points {\n  text-align: right;\n}\n\n[dir=\"rtl\"] .Opportunity-styles__points {\n  text-align: left;\n}\n\n.Opportunity-styles__pointsNumber {\n  display: block;\n  font-size: ").concat(theme.pointsNumberFontSize || 'inherit', ";\n}\n\n.Opportunity-styles__due {\n  margin-top: ").concat(theme.dueMargin || 'inherit', ";\n  font-size: ").concat(theme.dueFontSize || 'inherit', ";\n  color: ").concat(theme.secondaryColor || 'inherit', ";\n}\n\n.Opportunity-styles__dueText {\n  font-weight: ").concat(theme.dueTextFontWeight || 'inherit', ";\n}\n");
  },
  'root': 'Opportunity-styles__root',
  'oppNameAndTitle': 'Opportunity-styles__oppNameAndTitle',
  'oppName': 'Opportunity-styles__oppName',
  'title': 'Opportunity-styles__title',
  'close': 'Opportunity-styles__close',
  'footer': 'Opportunity-styles__footer',
  'status': 'Opportunity-styles__status',
  'points': 'Opportunity-styles__points',
  'pointsNumber': 'Opportunity-styles__pointsNumber',
  'due': 'Opportunity-styles__due',
  'dueText': 'Opportunity-styles__dueText'
};
import theme from './theme.js';
export class Opportunity extends Component {
  constructor(props) {
    super(props);

    this.linkRef = ref => {
      this.link = ref;
    };

    this.dismiss = () => {
      if (this.props.dismiss) {
        this.props.dismiss(this.props.id, this.props.plannerOverride);
      }
    };

    const tzMomentizedDate = moment.tz(props.dueAt, props.timeZone);
    this.fullDate = getFullDateAndTime(tzMomentizedDate);
  }

  componentDidMount() {
    this.props.registerAnimatable('opportunity', this, this.props.animatableIndex, [this.props.id]);
  }

  componentWillReceiveProps(newProps) {
    this.props.deregisterAnimatable('opportunity', this, [this.props.id]);
    this.props.registerAnimatable('opportunity', this, newProps.animatableIndex, [newProps.id]);
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('opportunity', this, [this.props.id]);
  }

  getFocusable() {
    return this.link;
  }

  renderButton() {
    const isDismissed = this.props.plannerOverride && this.props.plannerOverride.dismissed;
    return React.createElement("div", {
      className: styles.close
    }, isDismissed ? null : React.createElement(Button, {
      onClick: this.dismiss,
      variant: "icon",
      icon: IconXLine,
      size: "small",
      title: formatMessage('Dismiss {opportunityName}', {
        opportunityName: this.props.opportunityTitle
      })
    }, React.createElement(ScreenReaderContent, null, formatMessage('Dismiss {opportunityName}', {
      opportunityName: this.props.opportunityTitle
    }))));
  }

  renderPoints() {
    if (typeof this.props.points !== 'number') {
      return React.createElement(ScreenReaderContent, null, formatMessage('There are no points associated with this item'));
    }

    return React.createElement("div", {
      className: styles.points
    }, React.createElement(ScreenReaderContent, null, formatMessage('{points} points', {
      points: this.props.points
    })), React.createElement(PresentationContent, null, React.createElement("span", {
      className: styles.pointsNumber
    }, this.props.points), formatMessage('points')));
  }

  render() {
    return React.createElement("div", {
      className: styles.root
    }, React.createElement("div", {
      className: styles.oppNameAndTitle
    }, React.createElement("div", {
      className: styles.oppName
    }, this.props.courseName), React.createElement("div", {
      className: styles.title
    }, React.createElement(Button, {
      variant: "link",
      theme: {
        mediumPadding: '0',
        mediumHeight: 'normal'
      },
      href: this.props.url,
      buttonRef: this.linkRef
    }, this.props.opportunityTitle))), React.createElement("div", {
      className: styles.footer
    }, React.createElement("div", {
      className: styles.status
    }, React.createElement(Pill, {
      text: formatMessage('Missing'),
      variant: "danger"
    }), React.createElement("div", {
      className: styles.due
    }, React.createElement("span", {
      className: styles.dueText
    }, formatMessage('Due:')), " ", this.fullDate)), this.renderPoints()), this.renderButton());
  }

}
Opportunity.propTypes = {
  id: string.isRequired,
  dueAt: string.isRequired,
  points: number,
  courseName: string.isRequired,
  opportunityTitle: string.isRequired,
  timeZone: string.isRequired,
  url: string.isRequired,
  dismiss: func,
  plannerOverride: object,
  registerAnimatable: func,
  deregisterAnimatable: func,
  animatableIndex: number
};
Opportunity.defaultProps = {
  registerAnimatable: () => {},
  deregisterAnimatable: () => {},
  dismiss: () => {}
};
export default animatable(themeable(theme, styles)(Opportunity));